/**
 * Tax Category Codes according to UN/CEFACT Code List 5305
 * Source: UNTDID 5305 - Duty or tax or fee category code
 * Used in XRechnung, ZUGFeRD, and other electronic invoice formats
 */

export interface TaxCategoryInfo {
  code: string;              // Official tax category code
  name: string;              // English name
  germanName: string;        // German name (for German invoicing)
  description: string;       // Detailed description
  isCommon: boolean;         // Commonly used in EU invoicing
  isActive: boolean;         // Currently active
  examples: string[];        // Example use cases
  vatApplicable: boolean;    // Whether VAT applies
  percentRequired: boolean;  // Whether tax percent is required
}

export const TAX_CATEGORY_DATA: Record<string, TaxCategoryInfo> = {
  // Standard VAT Categories
  S: {
    code: 'S',
    name: 'Standard rate',
    germanName: 'Normaler Steuersatz',
    description: 'Standard VAT rate applies',
    isCommon: true,
    isActive: true,
    examples: ['Most goods and services', 'Standard business transactions'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Reduced VAT Categories
  AA: {
    code: 'AA',
    name: 'Lower rate',
    germanName: 'Ermäßigter Steuersatz',
    description: 'Reduced VAT rate applies',
    isCommon: true,
    isActive: true,
    examples: ['Food items', 'Books', 'Medical supplies', 'Hotel accommodation'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Zero-rated
  Z: {
    code: 'Z',
    name: 'Zero rated',
    germanName: 'Nullsatz',
    description: 'Zero percent VAT rate',
    isCommon: true,
    isActive: true,
    examples: ['Exports outside EU', 'International transport', 'Certain medical services'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Exempt Categories
  E: {
    code: 'E',
    name: 'Exempt from tax',
    germanName: 'Steuerbefreit',
    description: 'Exempt from VAT without credit',
    isCommon: true,
    isActive: true,
    examples: ['Financial services', 'Insurance', 'Medical services', 'Educational services'],
    vatApplicable: false,
    percentRequired: false
  },
  
  // Reverse Charge
  AE: {
    code: 'AE',
    name: 'Reverse charge',
    germanName: 'Umkehrung der Steuerschuldnerschaft',
    description: 'VAT reverse charge applies - buyer pays VAT',
    isCommon: true,
    isActive: true,
    examples: ['B2B construction services', 'Telecommunications', 'Electronic services', 'Scrap metal'],
    vatApplicable: true,
    percentRequired: false
  },
  
  // Intra-Community
  K: {
    code: 'K',
    name: 'Intra-Community supply',
    germanName: 'Innergemeinschaftliche Lieferung',
    description: 'Intra-Community supply (0% VAT)',
    isCommon: true,
    isActive: true,
    examples: ['Goods delivered to another EU country', 'EU B2B transactions'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Export
  G: {
    code: 'G',
    name: 'Export outside EU',
    germanName: 'Ausfuhr außerhalb der EU',
    description: 'Export outside the European Union',
    isCommon: true,
    isActive: true,
    examples: ['Goods exported to non-EU countries', 'International shipping'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Not subject to VAT
  O: {
    code: 'O',
    name: 'Not subject to VAT',
    germanName: 'Nicht steuerbar',
    description: 'Services not subject to VAT',
    isCommon: true,
    isActive: true,
    examples: ['Services outside EU territory', 'Non-taxable transactions'],
    vatApplicable: false,
    percentRequired: false
  },
  
  // Special Categories for Spanish Territories
  L: {
    code: 'L',
    name: 'Canary Islands general indirect tax',
    germanName: 'Kanarische Inseln allgemeine indirekte Steuer',
    description: 'IGIC (Impuesto General Indirecto Canario) applies',
    isCommon: false,
    isActive: true,
    examples: ['Transactions in Canary Islands'],
    vatApplicable: true,
    percentRequired: true
  },
  
  M: {
    code: 'M',
    name: 'Tax for production, services and importation in Ceuta and Melilla',
    germanName: 'Steuer für Produktion, Dienstleistungen und Einfuhr in Ceuta und Melilla',
    description: 'IPSI (Impuesto sobre la Producción, los Servicios y la Importación) applies',
    isCommon: false,
    isActive: true,
    examples: ['Transactions in Ceuta and Melilla'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Additional Categories
  B: {
    code: 'B',
    name: 'Transferred (VAT)',
    germanName: 'Übertragen (USt)',
    description: 'VAT transferred to another party',
    isCommon: false,
    isActive: true,
    examples: ['Specific transfer arrangements'],
    vatApplicable: true,
    percentRequired: false
  },
  
  F: {
    code: 'F',
    name: 'Deemed supply',
    germanName: 'Unentgeltliche Wertabgabe',
    description: 'Deemed supply for VAT purposes',
    isCommon: false,
    isActive: true,
    examples: ['Free samples', 'Gifts', 'Internal use'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Higher rate (for countries with multiple standard rates)
  H: {
    code: 'H',
    name: 'Higher rate',
    germanName: 'Höherer Steuersatz',
    description: 'Higher than standard VAT rate',
    isCommon: false,
    isActive: true,
    examples: ['Luxury goods', 'Special products with higher rate'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // Additional special categories
  C: {
    code: 'C',
    name: 'Compound',
    germanName: 'Zusammengesetzt',
    description: 'Compound tax rate',
    isCommon: false,
    isActive: true,
    examples: ['Combined tax rates'],
    vatApplicable: true,
    percentRequired: true
  },
  
  // No tax
  X: {
    code: 'X',
    name: 'No tax',
    germanName: 'Keine Steuer',
    description: 'No tax applicable',
    isCommon: false,
    isActive: true,
    examples: ['Tax-free transactions'],
    vatApplicable: false,
    percentRequired: false
  }
};

// Common tax categories for prioritized display
export const COMMON_TAX_CATEGORIES = ['S', 'AA', 'Z', 'E', 'AE', 'K', 'G', 'O'] as const;

// German VAT rates for validation
export const GERMAN_VAT_RATES = {
  STANDARD: 19,
  REDUCED: 7,
  ZERO: 0
} as const;

// Regional tax category groups
export const TAX_CATEGORY_GROUPS = {
  'Standard & Reduced': ['S', 'AA', 'H'],
  'Zero & Exempt': ['Z', 'E', 'O', 'X'],
  'Special Procedures': ['AE', 'K', 'G', 'B', 'F'],
  'Regional (Spain)': ['L', 'M'],
  'Other': ['C']
} as const;

// Get all active tax category codes
export const getAllTaxCategoryCodes = (): string[] => {
  return Object.keys(TAX_CATEGORY_DATA).filter(code => TAX_CATEGORY_DATA[code].isActive);
};

// Get tax category info by code
export const getTaxCategoryInfo = (code: string): TaxCategoryInfo | undefined => {
  return TAX_CATEGORY_DATA[code.toUpperCase()];
};

// Get tax category name by code
export const getTaxCategoryName = (code: string): string => {
  const category = getTaxCategoryInfo(code);
  return category?.name || code;
};

// Get German tax category name by code
export const getTaxCategoryGermanName = (code: string): string => {
  const category = getTaxCategoryInfo(code);
  return category?.germanName || code;
};

// Get formatted tax category display (code + name)
export const getFormattedTaxCategoryDisplay = (code: string): string => {
  const category = getTaxCategoryInfo(code);
  return category ? `${code} - ${category.name}` : code;
};

// Get formatted tax category display with German name
export const getFormattedTaxCategoryDisplayGerman = (code: string): string => {
  const category = getTaxCategoryInfo(code);
  return category ? `${code} - ${category.germanName}` : code;
};

// Check if tax category requires percentage
export const isTaxPercentRequired = (code: string): boolean => {
  const category = getTaxCategoryInfo(code);
  return category?.percentRequired || false;
};

// Check if VAT applies to tax category
export const isVATApplicable = (code: string): boolean => {
  const category = getTaxCategoryInfo(code);
  return category?.vatApplicable || false;
};

// Get tax categories by group
export const getTaxCategoriesByGroup = (group: keyof typeof TAX_CATEGORY_GROUPS): TaxCategoryInfo[] => {
  const categoryCodes = TAX_CATEGORY_GROUPS[group];
  return categoryCodes.map(code => TAX_CATEGORY_DATA[code]).filter(Boolean);
};

// Search tax categories by name or code
export const searchTaxCategories = (query: string): TaxCategoryInfo[] => {
  const searchTerm = query.toLowerCase();
  return Object.values(TAX_CATEGORY_DATA)
    .filter(category => 
      category.isActive && (
        category.name.toLowerCase().includes(searchTerm) ||
        category.germanName.toLowerCase().includes(searchTerm) ||
        category.code.toLowerCase().includes(searchTerm) ||
        category.description.toLowerCase().includes(searchTerm)
      )
    )
    .sort((a, b) => {
      // Prioritize exact matches
      const aExact = a.code.toLowerCase() === searchTerm;
      const bExact = b.code.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize common categories
      const aCommon = a.isCommon;
      const bCommon = b.isCommon;
      
      if (aCommon && !bCommon) return -1;
      if (!aCommon && bCommon) return 1;
      
      // Finally alphabetical
      return a.name.localeCompare(b.name);
    });
};

// Validate tax rate for German VAT
export const validateGermanVATRate = (rate: number, categoryCode: string): boolean => {
  const category = getTaxCategoryInfo(categoryCode);
  if (!category || !category.vatApplicable) return true;
  
  const validRates = [0, 7, 19];
  return validRates.includes(rate);
};

// Get suggested tax rate for category
export const getSuggestedTaxRate = (categoryCode: string, country: string = 'DE'): number | null => {
  const category = getTaxCategoryInfo(categoryCode);
  if (!category || !category.vatApplicable) return null;
  
  // German VAT rates
  if (country === 'DE') {
    switch (categoryCode) {
      case 'S': return GERMAN_VAT_RATES.STANDARD;
      case 'AA': return GERMAN_VAT_RATES.REDUCED;
      case 'Z': case 'K': case 'G': return GERMAN_VAT_RATES.ZERO;
      default: return null;
    }
  }
  
  return null;
};

export type CommonTaxCategory = typeof COMMON_TAX_CATEGORIES[number];
export type AllTaxCategory = keyof typeof TAX_CATEGORY_DATA;